import { globalLoggerToken } from "@/tokens";
import { applyTransaction, arrayAdd, arrayRemove } from "@datorama/akita";
import { Observable, Subject, Subscription } from "rxjs";
import { bufferTime, filter, map, mergeMap, tap } from "rxjs/operators";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import { WishlistsApiService } from "../api_services/WishlistsApiService/WishlistsApiService";
import { WishlistProductStatusesStore } from "../stores/WishlistProductStatusesStore";

@singleton()
export class WishlistProductStatusesService {
    private bufferedProductIds$: Observable<number[]>;
    private productIdBehavior$: Subject<number>;
    private subscription: Subscription | null = null;

    public constructor(
        private readonly wishlistsApiService: WishlistsApiService,
        private readonly wishlistProductStatusesStore: WishlistProductStatusesStore,
        @inject(globalLoggerToken) private readonly logger: LoggerInterface
    ) {
        this.productIdBehavior$ = new Subject<number>();
        this.bufferedProductIds$ = this.productIdBehavior$.pipe(
            bufferTime(1000)
        );
    }

    public addProductIdForFetching(productId: number) {
        this.logger.debug(
            "WishlistProductStatusesService.addProductIdForFetching",
            {
                productId,
            }
        );
        this.productIdBehavior$.next(productId);
    }

    public setProductStatusLoadingStatus(productId: number) {
        this.wishlistProductStatusesStore.setLoadingStatus(productId);
    }

    public unsetProductStatusLoadingStatus(productId: number) {
        this.wishlistProductStatusesStore.unsetLoadingStatus(productId);
    }

    public changeWishlistItemsCount(productId: number, delta: number) {
        this.wishlistProductStatusesStore.update(
            productId,
            ({ wishlistItemsCount }) => ({
                wishlistItemsCount: wishlistItemsCount + delta,
            })
        );
    }

    public setProductInWishlistStatus(
        productInWishlist: boolean,
        productId: number
    ) {
        this.wishlistProductStatusesStore.update(productId, () => ({
            productInWishlist,
        }));
    }

    public setVariantInWishlistStatus(
        isAddedToWishlist: boolean,
        productId: number,
        variantId: number
    ) {
        this.wishlistProductStatusesStore.update(
            productId,
            ({ variantsInWishlist }) => ({
                variantsInWishlist: isAddedToWishlist
                    ? arrayAdd(variantsInWishlist, variantId)
                    : arrayRemove(variantsInWishlist, variantId),
            })
        );
    }

    public clearVariantInWishlistStatus(productId: number) {
        this.wishlistProductStatusesStore.update(productId, (product) => ({
            ...product,
            variantsInWishlist: [],
        }));
    }

    public subscribe() {
        if (this.subscription) return;
        this.logger.debug("WishlistProductInfoService.subscribe");
        this.subscription = this.bufferedProductIds$
            .pipe(
                filter((ids) => ids.length >= 1),
                map((ids) =>
                    ids.filter((id, index) => ids.indexOf(id) === index)
                ),
                tap((ids) => {
                    applyTransaction(() => {
                        for (const id of ids) {
                            this.wishlistProductStatusesStore.setLoadingStatus(
                                id
                            );
                        }
                    });
                }),
                mergeMap((ids: number[]) => {
                    return this.wishlistsApiService.getWishlistProductStatuses({
                        productIds: ids,
                    });
                }),
                tap((results) => {
                    applyTransaction(() => {
                        results.forEach((result) => {
                            this.wishlistProductStatusesStore.unsetLoadingStatus(
                                result.productId
                            );
                            this.wishlistProductStatusesStore.add(result);
                        });
                    });
                })
            )
            .subscribe();
    }

    public stop() {
        this.logger.debug("WishlistProductInfoService.stop");
        this.subscription?.unsubscribe();
    }
}
